import { PropsWithChildren, useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useUpdateEffect } from "react-use";

import { IContextPolotnoTemplates } from "@/components/features/polotno_templates/context";
import { customGeneratePath } from "@/utils";

import { queryParams } from "../helpers/query_params";
import { ContextStickers } from "./stickers.context";
import { IContextStickers } from "./stickers.types";

export const ProviderStickers = ({
  pathPattern,
  children,
}: PropsWithChildren<{
  pathPattern: string;
}>) => {
  const navigate = useNavigate();

  const params = useParams<{ stickerId?: string }>();

  const [initialQueryParams] = useState(() => queryParams.deserialize());

  const [sortBy, setSortBy] = useState<IContextStickers["state"]["sortBy"]>(
    initialQueryParams.sort || "-created_at",
  );

  const activeStickerId = useMemo<IContextStickers["state"]["activeStickerId"]>(
    () =>
      !Number.isNaN(Number(params.stickerId))
        ? Number(params.stickerId)
        : undefined,
    [params.stickerId],
  );

  const [selectedTags, setSelectedTags] = useState<
    IContextStickers["state"]["selectedTags"]
  >(initialQueryParams.tags);

  const [searchString, setSearchString] = useState<string>(
    initialQueryParams.search || "",
  );

  const updateSelectedTags = useCallback<
    IContextPolotnoTemplates["actions"]["updateSelectedTags"]
  >((parentSlug, slug, clearBefore) => {
    setSelectedTags((state) => {
      let newState = clearBefore
        ? []
        : state.filter((tag) => !tag.startsWith(parentSlug));

      if (slug && !state.includes(slug)) {
        newState.push(slug);
      }

      return newState;
    });
  }, []);

  useUpdateEffect(() => {
    if (activeStickerId) {
      return;
    }

    navigate(
      {
        pathname: customGeneratePath(pathPattern),
        search: queryParams.serialize(searchString, selectedTags, sortBy),
      },
      {
        replace: true,
      },
    );
  }, [searchString, selectedTags, sortBy, activeStickerId]);

  return (
    <ContextStickers.Provider
      value={{
        pathPattern,
        state: {
          activeStickerId,
          sortBy,
          selectedTags,
          searchString,
        },
        actions: {
          setSortBy,
          setSelectedTags,
          setSearchString,
          updateSelectedTags,
        },
      }}
    >
      {children}
    </ContextStickers.Provider>
  );
};
